import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { useCopyAction } from "@repo/ui/clipboard/useCopyAction";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface CopyValueButtonProps {
  value: string;
  /** Names the value in the tooltip and for assistive tech ("Copy bucket name"). */
  label: string;
  className?: string;
}

/** The copy-to-clipboard control for a read-only value (see `SigningKeyDisplay`). */
export const CopyValueButton: FC<CopyValueButtonProps> = (props) => {
  const { value, label, className } = props;
  const { didJustCopy, onCopy } = useCopyAction(value);
  return (
    <TooltipTrigger>
      <Button
        variant={ButtonVariant.Tertiary}
        size={Size.MEDIUM}
        onClick={onCopy}
        className={twMerge(
          "shrink-0 rounded-sm p-1 text-muted-foreground hover:bg-gray-200 hover:text-gray-700",
          className,
        )}
      >
        {didJustCopy ? (
          <IconCheck className="size-4 text-success" />
        ) : (
          <IconCopy className="size-4" />
        )}
      </Button>
      <SimpleTooltip>{didJustCopy ? "Copied" : `Copy ${label}`}</SimpleTooltip>
    </TooltipTrigger>
  );
};

export interface CopyableValueProps {
  value: string;
  label: string;
}

/** A read-only value in a code box with a copy-to-clipboard control. */
export const CopyableValue: FC<CopyableValueProps> = (props) => {
  const { value, label } = props;
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 rounded-md border border-border-light bg-muted px-2.5 py-1.5 font-mono text-xs break-all text-foreground">
        {value}
      </code>
      <CopyValueButton value={value} label={label} />
    </div>
  );
};
